// plugins/collection-context-guard.client.ts — a tab whose loaded clinic no longer
// matches the profile's clinic reloads before any write (spec §4.3, M-AG-001).
// Three signals: the localStorage ping written by switchTo() in another tab
// (immediate), visibilitychange (tab comes back), and a 60 s interval while visible.
import { CONTEXT_STORAGE_KEY, parseContextPing, shouldReloadForContext } from '~/utils/collectionContext'
import { useCollectionContext } from '~/composables/useCollectionContext'

const CHECK_INTERVAL_MS = 60_000
const MIN_SERVER_CHECK_GAP_MS = 5_000

export default defineNuxtPlugin(() => {
  if (import.meta.server) return

  // Capture composables at setup time; the callbacks below only read .value.
  const supabase  = useSupabaseClient()
  const user      = useSupabaseUser()
  const authStore = useAuthStore()
  const toast     = useToast()
  const ctx       = useCollectionContext()

  let reloading = false
  let lastServerCheck = 0

  function reload() {
    if (reloading) return
    reloading = true
    // Neutral copy: the same reload fires when the SERVER moved her (suspension,
    // engagement revoked → _force_home), not only after a switch in another tab.
    toast.info('Tu clínica activa cambió. Recargando…')
    setTimeout(() => window.location.assign('/cobranza'), 800)
  }

  function onStorage(e: StorageEvent) {
    if (e.key !== CONTEXT_STORAGE_KEY) return
    const ping = parseContextPing(e.newValue)
    if (ping && shouldReloadForContext(authStore.clinicId, ping.clinic_id)) reload()
  }

  async function checkServer() {
    if (reloading || !user.value || !authStore.clinicId) return
    if (!ctx.isAgencyMember.value) return            // non-members never switch
    const now = Date.now()
    if (now - lastServerCheck < MIN_SERVER_CHECK_GAP_MS) return
    lastServerCheck = now
    try {
      const { data } = await supabase.from('profiles').select('clinic_id').eq('id', user.value.id).maybeSingle()
      const serverClinic = ((data as any)?.clinic_id ?? null) as string | null
      if (shouldReloadForContext(authStore.clinicId, serverClinic)) reload()
    } catch { /* offline / transient — next signal retries */ }
  }

  window.addEventListener('storage', onStorage)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkServer()
  })
  setInterval(() => {
    if (document.visibilityState === 'visible') void checkServer()
  }, CHECK_INTERVAL_MS)
})
